import {
  MoveContent,
  type MoveDetails,
  type MoveSizeDetails,
  type Room,
  SpecialItem,
} from '../quotes.types'
import { roomsByResidenceMatrix } from '../values/roomsByResidenceMatrix'
import { residenceTypeVolume } from '../values/residenceTypeVolume'
import { roomVolume } from '../values/roomVolume'
import { specialItemVolume } from '../values/specialItemVolume'
import { boxVolume } from '../quotes.const'

/**
 * returns approximate volume based on form values
 */
export const estimatePreliminaryVolume = ({
  residenceAndRoomsType,
  moveContent,
  nbBoxes = 0,
  furnitureRatio,
  nbAppliancesAndMore = 0,
}: MoveDetails) => {
  furnitureRatio ??= 100
  let volumeCubicFeet = 0

  /* boxes volume */
  if (moveContent.includes(MoveContent.Boxes)) {
    volumeCubicFeet += (nbBoxes ?? 0) * boxVolume
  }

  /* furniture volume */
  if (moveContent.includes(MoveContent.Furniture)) {
    const rooms = Object.entries(roomsByResidenceMatrix[residenceAndRoomsType] ?? {})
    const furnitureVolume = rooms.reduce<number>((acc, curr) => {
      const [roomType, roomQuantity] = curr
      return acc + ((roomVolume?.[roomType as Room] ?? 0) * (roomQuantity ?? 0))
    }, 0)
    volumeCubicFeet += ((furnitureVolume ?? 0) / 100) * furnitureRatio
  }

  /* known residence volume (storage units) */
  volumeCubicFeet += ((residenceTypeVolume[residenceAndRoomsType] ?? 0) / 100) * furnitureRatio

  /* appliances volume */
  if (moveContent.includes(MoveContent.ApplianceAndMore)) {
    volumeCubicFeet += (nbAppliancesAndMore ?? 0) * specialItemVolume[SpecialItem.Appliance] ?? 0
  }

  return Math.ceil(volumeCubicFeet)
}

/**
 * returns accurate volume based on all move details
 */
export const estimateVolume = (move: MoveSizeDetails) => {
  let volumeCubicFeet = 0
  const furnitureRatio = move.furnitureRatio ?? 100

  /* boxes volume */
  volumeCubicFeet += (move.nbBoxes ?? 0) * boxVolume

  /* furniture volume */
  if (move.residenceRooms) {
    move.residenceRooms.forEach(({ key, value }) => {
      volumeCubicFeet += (((roomVolume[key] ?? 0) * (value ?? 0)) / 100) * furnitureRatio
    })
  }

  /* known residence volume (storage units) */
  if (move.residenceType) {
    volumeCubicFeet += ((residenceTypeVolume[move.residenceType] ?? 0) / 100) * furnitureRatio
  }

  /* special items volume */
  if (move.specialItems) {
    move.specialItems.forEach(({ key, value }) => {
      volumeCubicFeet += (specialItemVolume[key] ?? 0) * (value ?? 0)
    })
  }

  return Math.ceil(volumeCubicFeet)
}
